import { useCallback, useState, useEffect } from 'react';
import { FlatList, ListRenderItem, Pressable, View } from 'react-native';
import { useQuery } from '@apollo/client/react';
import { Text } from '@/components/ui/text';
import { Icon } from '~/global/components/icon/Icon';
import { BaseModalProps, useModal } from '@/contexts/ModalContext';
import type { Meal } from '~/graphql/generated/graphql';
import {
  CurrentUserDocument,
  SearchMealsDocument,
  SpecialMealsDocument,
  SpecialMeal,
} from '~/graphql/generated/graphql';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
} from '@/components/ui/drawer';
import { useTranslation } from 'react-i18next';
import { Feature } from '@fytness/shared';
import { useFeatureGuard } from '~/purchases/hooks/useFeatureGuard';
import { MealSelectorSearch } from '../meal-selector-search/MealSelectorSearch';
import { MealFormDialog } from '../meal-form-dialog/MealFormDialog';

// "All" keeps the search-fed list; "Water" pins the single canonical plain-water
// entry (resolved server-side from the CMS-defined id, no search).
const FILTER_KEYS = ['all', 'water'] as const;
type FilterKey = (typeof FILTER_KEYS)[number];

export interface MealSelectorModalProps extends BaseModalProps {
  onMealSelected?: (meal: Meal) => void;
  portalHost?: string;
  barcode?: string;
  hideSearch?: boolean;
}

export const MealSelectorModal = ({
  onClose,
  onMealSelected,
  portalHost,
  barcode,
  hideSearch = false,
}: MealSelectorModalProps) => {
  const { t } = useTranslation('meal');
  const { showModal } = useModal();
  // Custom foods are a Pro feature — gate the create entry so free users get the paywall
  // (the SaveMeal mutation is also enforced server-side).
  const featureGuard = useFeatureGuard();
  const { data: userData } = useQuery(CurrentUserDocument);
  const currentUserId = userData?.currentUser?.id;
  const [searchResults, setSearchResults] = useState<Meal[]>([]);
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all');
  const isWaterFilter = activeFilter === 'water';

  // "Water" chip: the server resolves the canonical plain-water entry from the
  // CMS-defined id (app_settings.water_meal) — no search, no Meilisearch, and no
  // id hardcoded client-side. Null until the CMS points at a populated Meal.
  const { data: waterData } = useQuery(SpecialMealsDocument, {
    variables: { special: SpecialMeal.Water },
    skip: !isWaterFilter,
  });
  const plainWaterMeals = (waterData?.meals?.items ?? []) as Meal[];
  const plainWaterMeal = plainWaterMeals[0] ?? null;

  const { data: barcodeData } = useQuery(SearchMealsDocument, {
    variables: {
      query: {
        barcode,
        limit: 50,
        includeMine: true,
      },
    },
    skip: !barcode,
  });

  const handleResultsChange = useCallback((results: Meal[]) => {
    setSearchResults(results);
  }, []);

  const handleMealSelect = useCallback(
    (meal: Meal) => {
      onMealSelected?.(meal);
      onClose?.();
    },
    [onMealSelected],
  );

  useEffect(() => {
    if (barcode && barcodeData?.meals?.items) {
      const items = barcodeData.meals.items as Meal[];
      setSearchResults(items);
      if (items.length === 1) {
        handleMealSelect(items[0]);
      }
    }
  }, [barcode, barcodeData, handleMealSelect]);

  const handleOpenCreateDialog = useCallback(() => {
    showModal(
      MealFormDialog,
      {
        barcode,
        onMealSaved: (id: string) => {},
      },
      { portalHost },
    );
  }, [showModal, portalHost, barcode]);

  const handleEditMeal = useCallback(
    (meal: Meal) => {
      showModal(
        MealFormDialog,
        {
          mealId: meal.id,
          onMealSaved: (id: string) => {},
        },
        { portalHost },
      );
    },
    [showModal, portalHost],
  );

  const buildSubtitle = (item: Meal) => {
    const parts: string[] = [];
    if (item.brand) parts.push(item.brand);
    const kcal = item.nutritionalInfo?.energyKcal;
    if (kcal != null) parts.push(`${Math.round(kcal)} kcal/100g`);
    return parts.join(' • ') || undefined;
  };

  const renderMealItem: ListRenderItem<Meal> = ({ item }) => {
    const isOwner = item.user?.id === currentUserId;
    const isWater = !!plainWaterMeal && item.id === plainWaterMeal.id;
    const subtitle = buildSubtitle(item);

    return (
      <View
        testID={isWater ? 'water-pinned-entry' : undefined}
        className="flex flex-row items-center gap-3 px-5 py-3"
      >
        {/* Thumbnail */}
        <View
          className={`flex items-center justify-center rounded-lg w-12 h-12 ${
            isWater ? 'bg-macro-water-soft' : 'bg-surface-elevated'
          }`}
        >
          <Icon
            name={isWater ? 'Droplet' : 'UtensilsCrossed'}
            size={24}
            className={isWater ? 'text-macro-water' : 'text-text-muted'}
          />
        </View>

        {/* Content */}
        <View className="flex-1 gap-1">
          <Text
            testID="meal-result-name"
            className="text-sm font-inter-semibold text-text-primary"
          >
            {item.name}
          </Text>
          {subtitle && (
            <Text className="text-xs text-text-muted">{subtitle}</Text>
          )}
        </View>

        {/* Edit button (owner only) */}
        {isOwner && (
          <Pressable
            onPress={() => handleEditMeal(item)}
            className="flex items-center justify-center rounded-lg bg-surface-elevated w-8 h-8 active:opacity-70"
          >
            <Icon name="Pencil" size={16} className="text-text-muted" />
          </Pressable>
        )}

        {/* Add button */}
        <Pressable
          testID="meal-result-add"
          onPress={() => handleMealSelect(item)}
          className="flex items-center justify-center rounded-lg bg-accent w-8 h-8 active:opacity-70"
        >
          <Icon name="Plus" size={18} className="text-accent-text" />
        </Pressable>
      </View>
    );
  };

  const renderSeparator = () => <View className="h-px bg-border mx-5" />;

  return (
    <Drawer open={true} onOpenChange={() => onClose()}>
      <DrawerContent>
        <DrawerHeader onClose={onClose}>
          <Text className="text-lg font-sora-semibold text-text-primary">
            {t('mealSelector.title')}
          </Text>
        </DrawerHeader>

        <DrawerBody>
          {/* Search & Filters */}
          <View testID="meal-selector-modal" className="gap-3 px-5 pb-3">
            {!hideSearch && (
              <MealSelectorSearch onResultsChange={handleResultsChange} />
            )}
            {hideSearch && barcode && (
              <Text className="text-sm text-text-muted">
                {t('mealSelector.barcodeResults')}
              </Text>
            )}

            {/* Filter tags */}
            <View className="flex flex-row gap-2">
              <Pressable
                testID="meal-selector-create-custom"
                onPress={() =>
                  featureGuard(Feature.CUSTOM_FOODS, handleOpenCreateDialog)
                }
                className="flex-row items-center gap-1 rounded-lg bg-surface-elevated px-3 py-2 active:opacity-70"
              >
                <Icon name="Plus" size={12} className="text-text-primary" />
                <Text className="text-xs font-inter-medium text-text-primary">
                  {t('mealSelector.createCustom')}
                </Text>
              </Pressable>
              {FILTER_KEYS.map((key) => {
                const isActive = activeFilter === key;
                return (
                  <Pressable
                    key={key}
                    testID={`meal-selector-filter-${key}`}
                    onPress={() => setActiveFilter(key)}
                    className={`rounded-lg px-3 py-2 ${
                      isActive ? 'bg-accent' : 'bg-surface-elevated'
                    }`}
                  >
                    <Text
                      className={`text-xs font-inter-medium ${
                        isActive ? 'text-accent-text' : 'text-text-muted'
                      }`}
                    >
                      {t(`mealSelector.filters.${key}`)}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          {/* Results — "Water" pins the single plain-water entry; "All" is search-fed. */}
          <View className="flex-1">
            {(isWaterFilter ? plainWaterMeals : searchResults).length === 0 ? (
              <View className="flex-1 items-center justify-center">
                <Text className="text-text-muted">
                  {t('mealSelector.resultsPlaceholder')}
                </Text>
              </View>
            ) : (
              <FlatList
                data={isWaterFilter ? plainWaterMeals : searchResults}
                keyExtractor={(item) => item.id}
                renderItem={renderMealItem}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{ paddingBottom: 16 }}
                ItemSeparatorComponent={renderSeparator}
                // Let a tap on a result register while the search keyboard is open.
                // Without this the list defaults to "never": the first tap only
                // dismisses the keyboard, so adding a meal took two taps.
                keyboardShouldPersistTaps="handled"
              />
            )}
          </View>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
